import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { markFieldDirty, setCaseValue, updateCaseField } from "../redux/slice";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import {
  selectCaseEntityMode,
  selectCaseFilledFields,
  selectCaseValue,
} from "../redux/selectors";
import { useStorageSelectors } from "../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import { editStorageObject } from "../../../app/state/storageObjectSlice/actions/actions";
import { debounce } from "throttle-debounce";
import {
  generateCaseSolutionPromptAction,
  getAiScoringAction,
} from "../redux/actions";
import {
  CasesValue,
  TAiScoringRequest,
  TCaseFieldKeys,
} from "../../../services/api/instances/casesApi/types";

export const useCaseUpdater = () => {
  const dispatch = useAppDispatch();
  const caseValue = useAppSelector(selectCaseValue);
  const caseSolutionFields = useAppSelector(selectCaseFilledFields);
  const entityMode = useAppSelector(selectCaseEntityMode);
  const { selectedObject } = useStorageSelectors("cases");
  const [isTipConfirm, setIsTipConfirm] = useState<boolean>(false);

  const caseValueRef = useRef(caseValue);
  const selectedObjectRef = useRef(selectedObject);
  const scoringAbortRef = useRef<{ abort: () => void } | null>(null);

  useEffect(() => {
    caseValueRef.current = caseValue;
  }, [caseValue]);

  useEffect(() => {
    selectedObjectRef.current = selectedObject;
  }, [selectedObject]);

  useEffect(() => {
    if (isTipConfirm) {
      dispatch(getAiScoringAction(null));
      setIsTipConfirm(false);
    }
  }, [isTipConfirm, caseValue]);

  const handleClickTip = (path: string, value: string) => {
    setIsTipConfirm(true);
    dispatch(updateCaseField({ path, value }));
  };

  const debouncedScoring = useRef(
    debounce(2000, () => {
      const snapshot = caseValueRef.current;
      if (!snapshot) return;

      scoringAbortRef.current = dispatch(getAiScoringAction(null)) as any;
    }),
  ).current;

  const debouncedSave = useRef(
    debounce(2000, (fieldName?: string) => {
      const snapshot = caseValueRef.current;
      const object = selectedObjectRef.current;
      if (!snapshot || !object) return;
      if (
        fieldName &&
        fieldName === "name" &&
        snapshot.case_object?.case_info?.name === ""
      ) {
        const newValue: CasesValue = {
          ...snapshot,
          case_object: {
            ...snapshot.case_object,
            case_info: {
              ...snapshot.case_object?.case_info,
              name: selectedObject?.value?.case_object?.case_info?.name ?? "",
            },
          },
        };
        dispatch(setCaseValue(newValue));
      }
      dispatch(
        editStorageObject({
          id: object.id as string,
          name: snapshot.case_object?.case_info?.name,
          value: snapshot,
          service: "cases",
        }),
      );
    }),
  ).current;

  const handleChangeField = useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string,
      path: string,
    ) => {
      setIsTipConfirm(false);
      const value =
        typeof event === "string"
          ? event
          : event.target instanceof HTMLInputElement &&
              event.target.type === "checkbox"
            ? event.target.checked
            : event.target.value;
      const field = path.split(".").pop() as TCaseFieldKeys;

      dispatch(updateCaseField({ path, value }));
      dispatch(markFieldDirty(path));

      const isSaveOnlyField =
        field === "name" ||
        field === "feasibility" ||
        field === "personal_effectiveness" ||
        entityMode === "agent";

      if (isSaveOnlyField) {
        debouncedSave(field);
      } else {
        scoringAbortRef.current?.abort();
        debouncedScoring();
      }
    },
    [entityMode],
  );

  const handleGetCaseSolution = useCallback(() => {
    dispatch(
      generateCaseSolutionPromptAction(caseSolutionFields as TAiScoringRequest),
    );
  }, [caseValue]);

  return {
    handleChangeField,
    handleClickTip,
    handleGetCaseSolution,
    debouncedScoring,
    debouncedSave,
  };
};
